import {
  BellRing,
  Bookmark,
  BriefcaseBusiness,
  CheckCircle2,
  ExternalLink,
  Loader2,
  MapPin,
  Search,
  SlidersHorizontal,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { apiUrl } from "./api";
import "./jobs.css";

type JobMatch = { overall: number; skills: number; experience: number; education: number; location: number; workStyle: number; reasons: string[] };
type Job = {
  id: string;
  title: string;
  company: { name: string; sponsorshipStrength: string };
  location: string;
  workplaceType: string;
  salaryText?: string | null;
  description: string;
  skills: string[];
  applyUrl: string;
  sourceName: string;
  sponsorshipSignals: string[];
  match?: JobMatch | null;
  isSaved: boolean;
  applicationStatus?: string | null;
};
type JobProfile = {
  headline?: string | null;
  skills: string[];
  educationLevel?: string | null;
  yearsOfExperience: number;
  desiredLocations: string[];
  remotePreferred: boolean;
  needsSponsorship: boolean;
  minimumSalaryUsd?: number | null;
};
type Alert = { id: string; name: string; query?: string | null; location?: string | null; minimumMatch?: number | null; frequency: string; sponsorshipOnly: boolean; remoteOnly: boolean };
type Application = { id: string; jobId: string; jobTitle: string; company: string; status: string; notes?: string | null; nextActionAt?: string | null };
type Tab = "discover" | "saved" | "applications" | "alerts" | "profile";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(apiUrl(path), {
    credentials: "include",
    ...options,
    headers: options?.body ? { "Content-Type": "application/json", ...options.headers } : options?.headers,
  });
  if (response.status === 204) return undefined as T;
  const body = await response.json().catch(() => null) as T & { error?: string; title?: string };
  if (!response.ok) throw new Error(body?.error ?? body?.title ?? (response.status === 401 ? "Bu özellik için giriş yapmalısın." : "İşlem tamamlanamadı."));
  return body;
}

export default function JobsOverlay() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("discover");
  const [authenticated, setAuthenticated] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [saved, setSaved] = useState<Job[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [profile, setProfile] = useState<JobProfile | null>(null);
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [sponsorshipOnly, setSponsorshipOnly] = useState(true);
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const capture = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const jobsLink = target?.closest('a[href="#is-araci"]');
      const previewButton = target?.closest("#is-araci .job-row button");
      if (!jobsLink && !previewButton) return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
    };
    document.addEventListener("click", capture, true);
    return () => document.removeEventListener("click", capture, true);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const loadPrivate = useCallback(async () => {
    try {
      await request("/api/v1/auth/me");
      setAuthenticated(true);
      const [profileResponse, savedResponse, applicationResponse, alertResponse] = await Promise.all([
        request<JobProfile | undefined>("/api/v1/me/job-profile"),
        request<{ items: Job[] }>("/api/v1/me/saved-jobs"),
        request<{ items: Application[] }>("/api/v1/me/job-applications"),
        request<{ items: Alert[] }>("/api/v1/me/job-alerts"),
      ]);
      setProfile(profileResponse ?? null);
      setSaved(savedResponse.items);
      setApplications(applicationResponse.items);
      setAlerts(alertResponse.items);
    } catch {
      setAuthenticated(false);
      setProfile(null);
      setSaved([]);
      setApplications([]);
      setAlerts([]);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("query", query.trim());
      if (location.trim()) params.set("location", location.trim());
      params.set("sponsorshipOnly", String(sponsorshipOnly));
      params.set("remoteOnly", String(remoteOnly));
      params.set("limit", "60");
      const response = await request<{ items: Job[] }>(`/api/v1/jobs?${params}`);
      setJobs(response.items);
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "İlanlar yüklenemedi.");
    } finally {
      setBusy(false);
    }
  }, [location, query, remoteOnly, sponsorshipOnly]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      void loadPrivate();
      void loadJobs();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open, loadJobs, loadPrivate]);

  const visibleJobs = useMemo(() => tab === "saved" ? saved : jobs, [jobs, saved, tab]);

  function openLogin() {
    setOpen(false);
    const buttons = Array.from(document.querySelectorAll("button"));
    buttons.find((button) => button.textContent?.trim().startsWith("Giriş yap"))?.click();
  }

  async function toggleSave(job: Job) {
    if (!authenticated) return openLogin();
    setError(null);
    try {
      if (job.isSaved) await request(`/api/v1/me/saved-jobs/${job.id}`, { method: "DELETE" });
      else await request(`/api/v1/me/saved-jobs/${job.id}`, { method: "POST" });
      await Promise.all([loadJobs(), loadPrivate()]);
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "İlan kaydedilemedi.");
    }
  }

  async function trackApplication(job: Job) {
    if (!authenticated) return openLogin();
    try {
      await request("/api/v1/me/job-applications", { method: "POST", body: JSON.stringify({ jobId: job.id, status: "applied" }) });
      await Promise.all([loadJobs(), loadPrivate()]);
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "Başvuru takibe alınamadı.");
    }
  }

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!authenticated) return openLogin();
    const values = Object.fromEntries(new FormData(event.currentTarget));
    const payload = {
      headline: values.headline || null,
      skills: String(values.skills ?? "").split(",").map(x => x.trim()).filter(Boolean),
      educationLevel: values.educationLevel || null,
      yearsOfExperience: Number(values.yearsOfExperience || 0),
      desiredLocations: String(values.desiredLocations ?? "").split(",").map(x => x.trim()).filter(Boolean),
      remotePreferred: values.remotePreferred === "on",
      needsSponsorship: values.needsSponsorship === "on",
      minimumSalaryUsd: values.minimumSalaryUsd ? Number(values.minimumSalaryUsd) : null,
    };
    setBusy(true);
    try {
      setProfile(await request<JobProfile>("/api/v1/me/job-profile", { method: "PUT", body: JSON.stringify(payload) }));
      await loadJobs();
      setTab("discover");
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "Profil kaydedilemedi.");
    } finally { setBusy(false); }
  }

  async function createAlert(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!authenticated) return openLogin();
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await request("/api/v1/me/job-alerts", {
        method: "POST",
        body: JSON.stringify({
          name: values.name,
          query: values.query || null,
          location: values.location || null,
          sponsorshipOnly: values.sponsorshipOnly === "on",
          remoteOnly: values.remoteOnly === "on",
          minimumMatch: values.minimumMatch ? Number(values.minimumMatch) : null,
          frequency: values.frequency || "daily",
        }),
      });
      event.currentTarget.reset();
      await loadPrivate();
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "Alarm oluşturulamadı.");
    }
  }

  async function deleteAlert(id: string) {
    await request(`/api/v1/me/job-alerts/${id}`, { method: "DELETE" });
    await loadPrivate();
  }

  async function updateApplication(item: Application, status: string) {
    await request(`/api/v1/me/job-applications/${item.id}`, { method: "PATCH", body: JSON.stringify({ status, notes: item.notes ?? null }) });
    await loadPrivate();
  }

  if (!open) return null;

  return (
    <div className="jobs-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setOpen(false)}>
      <section className="jobs-shell" role="dialog" aria-modal="true" aria-label="Amerikolay İş Radarı">
        <header className="jobs-header">
          <div><span><BriefcaseBusiness size={18} /> AMERİKOLAY İŞ RADARI</span><h2>Sana açık işleri bul, kaydet ve takip et.</h2></div>
          <button className="jobs-close" onClick={() => setOpen(false)} aria-label="Kapat"><X /></button>
        </header>

        <nav className="jobs-tabs">
          <button className={tab === "discover" ? "active" : ""} onClick={() => setTab("discover")}><Search /> Keşfet</button>
          <button className={tab === "saved" ? "active" : ""} onClick={() => authenticated ? setTab("saved") : openLogin()}><Bookmark /> Kaydedilenler</button>
          <button className={tab === "applications" ? "active" : ""} onClick={() => authenticated ? setTab("applications") : openLogin()}><BriefcaseBusiness /> Başvurular</button>
          <button className={tab === "alerts" ? "active" : ""} onClick={() => authenticated ? setTab("alerts") : openLogin()}><BellRing /> Alarmlar</button>
          <button className={tab === "profile" ? "active" : ""} onClick={() => authenticated ? setTab("profile") : openLogin()}><UserRound /> Profil</button>
        </nav>

        {error && <div className="jobs-error">{error}</div>}

        {(tab === "discover" || tab === "saved") && (
          <div className="jobs-content-grid">
            <aside className="jobs-filters">
              <h3><SlidersHorizontal /> Filtreler</h3>
              <label>Arama<input value={query} onChange={(e) => setQuery(e.target.value)} placeholder=".NET, data, engineer..." /></label>
              <label>Konum<input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Texas, New York..." /></label>
              <label className="jobs-check"><input type="checkbox" checked={sponsorshipOnly} onChange={(e) => setSponsorshipOnly(e.target.checked)} /> Sponsorluk sinyali</label>
              <label className="jobs-check"><input type="checkbox" checked={remoteOnly} onChange={(e) => setRemoteOnly(e.target.checked)} /> Sadece remote</label>
              <button className="jobs-primary" onClick={() => void loadJobs()}>{busy ? <Loader2 className="jobs-spin" /> : <Search />} İlanları yenile</button>
              {authenticated && profile ? <div className="jobs-profile-ready"><CheckCircle2 /> Profil eşleştirmesi aktif</div> : <button className="jobs-profile-cta" onClick={() => authenticated ? setTab("profile") : openLogin()}><Sparkles /> Kişisel eşleşmeyi aç</button>}
            </aside>

            <div className="jobs-results">
              <div className="jobs-results-head"><strong>{visibleJobs.length} ilan</strong><span>Sponsorluk sinyali garanti değildir.</span></div>
              {busy && !visibleJobs.length ? <div className="jobs-loading"><Loader2 className="jobs-spin" /> İlanlar yükleniyor...</div> : visibleJobs.map((job) => (
                <article className="jobs-card" key={job.id}>
                  <div className="jobs-card-top">
                    <div className="jobs-company">{job.company.name.slice(0, 1)}</div>
                    <div className="jobs-card-title"><h3>{job.title}</h3><p>{job.company.name} · <MapPin size={13} /> {job.location} · {job.workplaceType}</p></div>
                    {job.match && <div className="jobs-match"><strong>{job.match.overall}</strong><span>% uyum</span></div>}
                  </div>
                  <div className="jobs-skill-row">{job.skills.slice(0, 6).map(skill => <span key={skill}>{skill}</span>)}</div>
                  {job.sponsorshipSignals.length > 0 && <div className="jobs-sponsor"><CheckCircle2 /> {job.sponsorshipSignals[0]}</div>}
                  {job.match && <ul className="jobs-reasons">{job.match.reasons.slice(0, 3).map(reason => <li key={reason}>{reason}</li>)}</ul>}
                  <div className="jobs-card-actions">
                    <strong>{job.salaryText || "Maaş belirtilmemiş"}</strong>
                    <button onClick={() => void toggleSave(job)}><Bookmark /> {job.isSaved ? "Kaydedildi" : "Kaydet"}</button>
                    <button onClick={() => void trackApplication(job)}><BriefcaseBusiness /> {job.applicationStatus ? "Takipte" : "Başvurdum"}</button>
                    <a href={job.applyUrl} target="_blank" rel="noreferrer">İlana git <ExternalLink /></a>
                  </div>
                </article>
              ))}
              {!busy && !visibleJobs.length && <div className="jobs-empty">Bu filtrelerle eşleşen ilan bulunamadı.</div>}
            </div>
          </div>
        )}

        {tab === "profile" && (
          <form className="jobs-form-panel" onSubmit={saveProfile}>
            <div><span><Sparkles /> KİŞİSEL EŞLEŞME</span><h3>İş arama profilin</h3><p>Bu bilgiler yalnızca sana özel iş puanı ve alarm filtreleri oluşturmak için kullanılır.</p></div>
            <label>Hedef unvan<input name="headline" defaultValue={profile?.headline ?? ""} placeholder="Senior Software Engineer" /></label>
            <label>Beceriler<input name="skills" defaultValue={profile?.skills.join(", ") ?? ""} placeholder=".NET, React, AWS, SQL" /></label>
            <div className="jobs-form-row"><label>Eğitim<select name="educationLevel" defaultValue={profile?.educationLevel ?? ""}><option value="">Seç</option><option>Bachelor</option><option>Master</option><option>Doctorate</option></select></label><label>Deneyim (yıl)<input name="yearsOfExperience" type="number" min="0" max="80" defaultValue={profile?.yearsOfExperience ?? 0} /></label></div>
            <label>Tercih edilen konumlar<input name="desiredLocations" defaultValue={profile?.desiredLocations.join(", ") ?? ""} placeholder="Austin, New York, Remote" /></label>
            <label>Minimum yıllık maaş ($)<input name="minimumSalaryUsd" type="number" min="0" defaultValue={profile?.minimumSalaryUsd ?? ""} /></label>
            <div className="jobs-form-row jobs-checkboxes"><label><input name="remotePreferred" type="checkbox" defaultChecked={profile?.remotePreferred} /> Remote tercih ediyorum</label><label><input name="needsSponsorship" type="checkbox" defaultChecked={profile?.needsSponsorship ?? true} /> Sponsorluk gerekiyor</label></div>
            <button className="jobs-primary" disabled={busy}>{busy ? <Loader2 className="jobs-spin" /> : <CheckCircle2 />} Profili kaydet</button>
          </form>
        )}

        {tab === "alerts" && (
          <div className="jobs-two-column">
            <form className="jobs-form-panel compact" onSubmit={createAlert}>
              <div><span><BellRing /> İŞ ALARMI</span><h3>Yeni alarm oluştur</h3></div>
              <label>Alarm adı<input name="name" required placeholder="Remote .NET + sponsor" /></label>
              <label>Arama<input name="query" placeholder=".NET" /></label>
              <label>Konum<input name="location" placeholder="Texas" /></label>
              <label>Minimum uyum<input name="minimumMatch" type="number" min="0" max="100" placeholder="80" /></label>
              <label>Sıklık<select name="frequency"><option value="instant">Saatlik</option><option value="daily">Günlük</option><option value="weekly">Haftalık</option></select></label>
              <label className="jobs-check"><input name="sponsorshipOnly" type="checkbox" defaultChecked /> Sponsorluk sinyali gerekli</label>
              <label className="jobs-check"><input name="remoteOnly" type="checkbox" /> Sadece remote</label>
              <button className="jobs-primary"><BellRing /> Alarmı oluştur</button>
            </form>
            <div className="jobs-list-panel"><h3>Aktif alarmlar</h3>{alerts.map(alert => <div className="jobs-list-item" key={alert.id}><div><strong>{alert.name}</strong><span>{alert.query || "Tüm işler"} · {alert.location || "Tüm ABD"} · {alert.frequency}</span></div><button onClick={() => void deleteAlert(alert.id)}>Sil</button></div>)}{!alerts.length && <p>Henüz iş alarmın yok.</p>}</div>
          </div>
        )}

        {tab === "applications" && (
          <div className="jobs-list-panel applications"><h3>Başvuru takip panosu</h3>{applications.map(item => <div className="jobs-list-item application" key={item.id}><div><strong>{item.jobTitle}</strong><span>{item.company}</span></div><select value={item.status} onChange={(event) => void updateApplication(item, event.target.value)}><option value="saved">Kaydedildi</option><option value="applied">Başvuruldu</option><option value="interview">Görüşme</option><option value="offer">Teklif</option><option value="rejected">Olumsuz</option><option value="withdrawn">Vazgeçildi</option></select></div>)}{!applications.length && <p>Takip ettiğin başvuru yok. Bir ilanda “Başvurdum” dediğinde burada görünür.</p>}</div>
        )}
      </section>
    </div>
  );
}
